import { useEffect, useMemo, useState } from 'react';
import { Button, Menu, MenuItem, SxProps, Theme } from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';

import usFlag from 'assets/images/us-flag.svg';
import mxFlag from 'assets/images/mx-flag.svg';
import brFlag from 'assets/images/br-flag.svg';
import frFlag from 'assets/images/fr-flag.svg';
import zhFlag from 'assets/images/zh-flag.svg';
import ruFlag from 'assets/images/ru-flag.svg';
import hiFlag from 'assets/images/hi-flag.svg';
import arFlag from 'assets/images/ar-flag.svg';

import styles from './LanguageSelector.module.scss';

export const LANGUAGE_SELECTOR_WIDTH_DESKTOP = 128;
export const LANGUAGE_SELECTOR_WIDTH_MOBILE = 120;

type LanguageSelectorProps = {
  width: number;
  sx?: SxProps<Theme>;
  compact?: boolean;
  compactShowLabel?: boolean;
  compactMenuWidth?: number;
  menuAlign?: 'center' | 'right';
  denseMenuItems?: boolean;
};

export function LanguageSelector({
  width,
  sx,
  compact = false,
  compactShowLabel = false,
  compactMenuWidth = 128,
  menuAlign = 'center',
  denseMenuItems = false,
}: LanguageSelectorProps) {
  const { i18n } = useTranslation();
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const isMenuOpen = Boolean(menuAnchorEl);
  const selectedLanguage =
    i18n.resolvedLanguage === 'es' ||
    i18n.resolvedLanguage === 'br' ||
    i18n.resolvedLanguage === 'fr' ||
    i18n.resolvedLanguage === 'zh' ||
    i18n.resolvedLanguage === 'ru' ||
    i18n.resolvedLanguage === 'hi' ||
    i18n.resolvedLanguage === 'ar'
      ? i18n.resolvedLanguage
      : 'en';
  const activeLanguage = (i18n.resolvedLanguage || i18n.language || selectedLanguage || 'en').toLowerCase();
  const isRtl = activeLanguage === 'ar' || activeLanguage.startsWith('ar-') || i18n.dir() === 'rtl';

  const languageOptions = useMemo<
    Array<{ code: 'en' | 'es' | 'br' | 'fr' | 'zh' | 'ru' | 'hi' | 'ar'; label: string; flag: string }>
  >(
    () => [
      { code: 'en', label: 'English', flag: usFlag },
      { code: 'es', label: 'Español', flag: mxFlag },
      { code: 'br', label: 'Português', flag: brFlag },
      { code: 'fr', label: 'Français', flag: frFlag },
      { code: 'zh', label: '中文', flag: zhFlag },
      { code: 'ru', label: 'Русский', flag: ruFlag },
      { code: 'hi', label: 'हिन्दी', flag: hiFlag },
      { code: 'ar', label: 'العربية', flag: arFlag },
    ],
    [],
  );

  const selectedLanguageOption =
    languageOptions.find((option) => option.code === selectedLanguage) || languageOptions[0];

  useEffect(() => {
    languageOptions.forEach((option) => {
      const image = new Image();
      image.src = option.flag;
    });
  }, [languageOptions]);

  const handleLanguageChange = (language: 'en' | 'es' | 'br' | 'fr' | 'zh' | 'ru' | 'hi' | 'ar') => {
    i18n.changeLanguage(language);
    setMenuAnchorEl(null);
  };

  return (
    <>
      <Button
        variant='outlined'
        className={classnames(styles.button, {
          [styles.compactButton]: compact,
        })}
        sx={{
          width: `${width}px`,
          minWidth: `${width}px`,
          maxWidth: `${width}px`,
          ...(isMenuOpen
            ? {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
                borderBottomColor: 'transparent',
              }
            : {}),
          ...sx,
        }}
        onClick={(e) => setMenuAnchorEl(e.currentTarget)}
        startIcon={<img src={selectedLanguageOption.flag} alt='' className={styles.flag} />}
        endIcon={<KeyboardArrowDown className={styles.arrow} />}
      >
        {!compact || compactShowLabel ? selectedLanguageOption.label : null}
      </Button>
      <Menu
        anchorEl={menuAnchorEl}
        open={isMenuOpen}
        onClose={() => setMenuAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: menuAlign }}
        transformOrigin={{ vertical: 'top', horizontal: menuAlign }}
        MenuListProps={{
          disablePadding: true,
        }}
        PaperProps={{
          sx: {
            width: compact ? 'max-content' : menuAnchorEl ? `${menuAnchorEl.clientWidth}px` : undefined,
            minWidth: compact ? `${compactMenuWidth}px` : menuAnchorEl ? `${menuAnchorEl.clientWidth}px` : undefined,
            maxWidth: compact ? '185px' : undefined,
            boxSizing: 'border-box',
            marginTop: '-1px',
            borderTop: 'none',
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
            '& .MuiMenuItem-root:hover': {
              backgroundColor: 'rgba(25, 118, 210, 0.08)',
            },
            '& .MuiMenuItem-root.Mui-selected': {
              backgroundColor: 'rgba(25, 118, 210, 0.14)',
            },
            '& .MuiMenuItem-root.Mui-selected:hover': {
              backgroundColor: 'rgba(25, 118, 210, 0.2)',
            },
          },
        }}
      >
        {languageOptions.map((option) => (
          <MenuItem
            key={option.code}
            className={styles.menuItem}
            onClick={() => handleLanguageChange(option.code)}
            selected={selectedLanguage === option.code}
            disableGutters={denseMenuItems}
            sx={{
              display: 'flex',
              width: '100%',
              boxSizing: 'border-box',
              whiteSpace: 'nowrap',
              direction: 'ltr',
              flexDirection: isRtl ? 'row-reverse' : 'row',
              justifyContent: 'flex-start',
              textAlign: isRtl ? 'right' : 'left',
              gap: denseMenuItems ? '2px' : '8px',
              [`& .${styles.menuFlag}`]: {
                margin: 0,
              },
              [`& .${styles.menuLabel}`]: {
                textAlign: isRtl ? 'right' : 'left',
              },
              ...(denseMenuItems
                ? {
                    pl: isRtl ? '12px !important' : '12px !important',
                    pr: isRtl ? '16px !important' : '12px !important',
                    gap: '8px',
                    [`& .${styles.menuFlag}`]: {
                      marginLeft: 0,
                      marginRight: 0,
                      marginInlineStart: 0,
                      marginInlineEnd: 0,
                    },
                  }
                : {}),
            }}
          >
            <img src={option.flag} alt='' className={styles.menuFlag} />
            <span className={styles.menuLabel}>{option.label}</span>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
